import { create } from 'zustand';

export type ToastTone = 'success' | 'error' | 'warn' | 'info';

export interface Toast {
  readonly id: string;
  readonly message: string;
  readonly tone: ToastTone;
}

interface ToastState {
  readonly toasts: readonly Toast[];
  push: (message: string, tone?: ToastTone) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const TOAST_TIMEOUT_MS = 3200;

/**
 * Notifications live in a store rather than a context so non-component code —
 * mutation callbacks, the API layer — can raise one without prop drilling.
 */
export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (message, tone = 'success') => {
    const id = globalThis.crypto.randomUUID();
    set((state) => ({ toasts: [...state.toasts, { id, message, tone }] }));
    return id;
  },
  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
  clear: () => {
    set({ toasts: [] });
  },
}));

/** Imperative entry point for code outside the React tree. */
export const toast = {
  success: (message: string) => useToastStore.getState().push(message, 'success'),
  error: (message: string) => useToastStore.getState().push(message, 'error'),
  warn: (message: string) => useToastStore.getState().push(message, 'warn'),
  info: (message: string) => useToastStore.getState().push(message, 'info'),
};
